<script setup lang="ts">
import { computed } from 'vue';
import { AkTooltipInformation, AkHeading4, AkParagraph, AkButton } from '@ankorstore/design-system';
import FilterIcon from '@client/modules/discovery/ui/filters/filter-icon.vue';
import { ItemIconConfig } from '@client/modules/discovery/ui/filters/types';
import { URL_LINKS } from '@client/modules/discovery/ui/filters/constants';
import { getLang } from '@core/utilities/lang';
import { CustomRouterLink } from '@bc/shared/util';

const props = defineProps<{
  iconConfig: ItemIconConfig;
  keyName?: string;
}>();

const links = computed(() => {
  return props.keyName ? URL_LINKS[props.keyName] : null;
});

const isRouteLink = computed(() => {
  return links.value && 'name' in links.value;
});

const urlLink = computed(() => {
  if (!links.value) {return null;}

  if (isRouteLink.value) {
    return links.value;
  }

  return links.value[getLang()];
});
</script>

<template>
  <AkTooltipInformation
    placement="right"
    trigger="hover"
    type="invert"
    data-testid="filterIconWithTooltip"
  >
    <slot>
      <FilterIcon
        :icon-config="iconConfig"
        data-testid="filterIcon"
      />
    </slot>
    <template #container>
      <AkHeading4 class="tooltip__content-title">
        {{ iconConfig.tooltip.title }}
      </AkHeading4>
      <AkParagraph
        size="sm"
        class="tooltip__content-paragraph"
      >
        {{ iconConfig.tooltip.text }}
      </AkParagraph>
      <a
        v-if="urlLink && !isRouteLink"
        :href="urlLink"
        target="_blank"
        data-testid="filterIconWithTooltipLink"
      >
        <AkButton
          class="tooltip__link"
          color="white"
        >
          {{ iconConfig.tooltip.actionText }}
        </AkButton>
      </a>
      <CustomRouterLink
        v-else-if="urlLink && isRouteLink"
        :to="urlLink"
        data-testid="filterIconWithTooltipRouterLink"
      >
        <AkButton
          class="tooltip__link"
          color="white"
        >
          {{ iconConfig.tooltip.actionText }}
        </AkButton>
      </CustomRouterLink>
    </template>
  </AkTooltipInformation>
</template>

<style lang="scss" scoped>
.tooltip {
  &__link {
    @apply ds-mt-2;
  }
}
</style>
